import { getAccountNumberError, getRoutingNumberError } from '@steerfi/shared';
import { AlertCircle } from 'lucide-react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import {
  type AddressVerificationResponse,
  type StandardizedAddress,
  verifyAddress,
} from '@/api/address';
import {
  AddressFormFields,
  AddressVerificationDialog,
  type AddressVerificationStatus,
} from '@/components/address';
import { getFieldValidationError } from '../utils/field-validators';
import { Button } from './ui/button';
import {
  FormControl,
  type FormErrorItem,
  FormErrorSummary,
  FormField,
  focusFirstInvalid,
} from './ui/form-field';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

export type PaymentMethodOption = 'ACH' | 'CHECK' | 'DIGITAL_WALLET';

export interface BeneficiaryFormValues {
  caseId: string;
  firstName: string;
  lastName: string;
  email: string;
  street: string;
  address2: string;
  city: string;
  state: string;
  zipCode: string;
  addressVerificationStatus: AddressVerificationStatus;
  paymentMethod: PaymentMethodOption | '';
  bankRoutingNumber: string;
  bankAccountNumber: string;
  bankAccountType: 'CHECKING' | 'SAVINGS';
}

const INITIAL_VALUES: BeneficiaryFormValues = {
  caseId: '',
  firstName: '',
  lastName: '',
  email: '',
  street: '',
  address2: '',
  city: '',
  state: '',
  zipCode: '',
  addressVerificationStatus: 'not_checked',
  paymentMethod: '',
  bankRoutingNumber: '',
  bankAccountNumber: '',
  bankAccountType: 'CHECKING',
};

interface BeneficiaryFormProps {
  mode: 'create' | 'edit';
  initialValues?: Partial<BeneficiaryFormValues>;
  onSubmit: (values: BeneficiaryFormValues) => Promise<void>;
  onCancel?: () => void;
  submitLabel: string;
  isSubmitting: boolean;
  error: string | null;
  disabled?: boolean;
  /** Pass with accountId so address verification is persisted to account_verifications */
  partyId?: string | null;
  /** Pass with partyId so address verification is persisted to account_verifications */
  accountId?: string | null;
  /** Label for the external ref / case ID field. Defaults to 'External Reference'. */
  externalRefLabel?: string;
}

function SectionHeader({ children, first }: { children: React.ReactNode; first?: boolean }) {
  return (
    <div className={first ? '' : 'border-t pt-5'}>
      <h2 className="text-lg font-semibold tracking-tight text-foreground">{children}</h2>
    </div>
  );
}

const BENEFICIARY_FIELD_LABELS: Record<string, string> = {
  caseId: 'Case ID',
  firstName: 'First Name',
  lastName: 'Last Name',
  paymentMethod: 'Payment Method',
  email: 'Email',
  street: 'Street Address',
  city: 'City',
  state: 'State',
  zipCode: 'ZIP Code',
  bankRoutingNumber: 'Bank Routing Number',
  bankAccountNumber: 'Account Number',
};

function errorsToSummaryItems(
  errors: Record<string, string>,
  formError?: string | null
): FormErrorItem[] {
  const items = Object.entries(errors).map(([key, message]) => ({
    label: BENEFICIARY_FIELD_LABELS[key] ?? key,
    message,
  }));
  if (formError) {
    items.push({ label: 'Form', message: formError });
  }
  return items;
}

export const BeneficiaryForm: React.FC<BeneficiaryFormProps> = ({
  mode,
  initialValues,
  onSubmit,
  onCancel,
  submitLabel,
  isSubmitting,
  error,
  disabled = false,
  partyId,
  accountId,
  externalRefLabel = 'External Reference',
}) => {
  const [form, setForm] = useState<BeneficiaryFormValues>({
    ...INITIAL_VALUES,
    ...initialValues,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitAttempt, setSubmitAttempt] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [addressVerificationResult, setAddressVerificationResult] =
    useState<AddressVerificationResponse | null>(null);

  // `error` is an API failure the parent owns (it makes the actual submit
  // call); announce and focus it the same as a client-side validation
  // failure, rather than leaving it silent with no live-region update.
  useEffect(() => {
    if (error) {
      setSubmitAttempt((n) => n + 1);
    }
  }, [error]);

  useEffect(() => {
    if (initialValues) {
      setForm((prev) => ({ ...prev, ...initialValues }));
    }
  }, [initialValues]);

  const getFieldError = (field: keyof BeneficiaryFormValues, value: string): string | null =>
    getFieldValidationError(field, value, form.paymentMethod);

  const updateField = (field: keyof BeneficiaryFormValues, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (
      form.addressVerificationStatus !== 'not_checked' &&
      ['street', 'address2', 'city', 'state', 'zipCode'].includes(field)
    ) {
      setForm((prev) => ({ ...prev, addressVerificationStatus: 'not_checked' }));
    }
    const error = getFieldError(field, value);
    setErrors((prev) => {
      const next = { ...prev };
      if (error) {
        next[field] = error;
      } else {
        delete next[field];
      }
      return next;
    });
  };

  const handleAddressChange = (
    field: 'address1' | 'address2' | 'city' | 'state' | 'zipCode',
    value: string
  ) => {
    const map: Record<string, keyof BeneficiaryFormValues> = {
      address1: 'street',
      address2: 'address2',
      city: 'city',
      state: 'state',
      zipCode: 'zipCode',
    };
    updateField(map[field], value);
  };

  const handleAddressVerificationResult = (result: AddressVerificationResponse) => {
    setAddressVerificationResult(result);
    if (result.hasCorrections || !result.verified) {
      setShowAddressDialog(true);
    }
  };

  const handleAcceptSuggestedAddress = async (suggested: StandardizedAddress) => {
    setForm((prev) => ({
      ...prev,
      street: suggested.streetAddress,
      address2: suggested.secondaryAddress ?? '',
      city: suggested.city,
      state: suggested.state,
      zipCode: suggested.zipCode,
      addressVerificationStatus: 'verified',
    }));
    setShowAddressDialog(false);

    // Re-verify with the suggested address so the backend persists VERIFIED (no corrections this time)
    try {
      await verifyAddress({
        streetAddress: suggested.streetAddress,
        secondaryAddress: suggested.secondaryAddress ?? null,
        city: suggested.city,
        state: suggested.state,
        zipCode: suggested.zipCode,
        ...(partyId && { partyId }),
        ...(accountId && { accountId }),
      });
    } catch {
      // Form is already updated; persist failure is non-blocking
    }
  };

  const handleKeepOriginalAddress = () => {
    setForm((prev) => ({
      ...prev,
      addressVerificationStatus: addressVerificationResult?.verified ? 'verified' : 'not_verified',
    }));
    setShowAddressDialog(false);
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!form.caseId.trim()) newErrors.caseId = `Enter a ${externalRefLabel.toLowerCase()}`;
    if (!form.firstName.trim()) {
      newErrors.firstName = 'Enter a first name';
    } else if (!/[a-zA-Z]/.test(form.firstName)) {
      newErrors.firstName = 'Enter a first name that includes at least one letter';
    }
    if (!form.lastName.trim()) {
      newErrors.lastName = 'Enter a last name';
    } else if (!/[a-zA-Z]/.test(form.lastName)) {
      newErrors.lastName = 'Enter a last name that includes at least one letter';
    }
    if (!form.paymentMethod) newErrors.paymentMethod = 'Select a payment method';
    if (!form.email.trim()) {
      newErrors.email = 'Enter an email like name@company.com';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email)) {
      newErrors.email = 'Enter an email like name@company.com';
    }
    if (form.paymentMethod === 'ACH') {
      const routingError = getRoutingNumberError(form.bankRoutingNumber);
      if (routingError) newErrors.bankRoutingNumber = routingError;
      const accountError = getAccountNumberError(form.bankAccountNumber);
      if (accountError) newErrors.bankAccountNumber = accountError;
    }

    if (!form.street.trim()) {
      newErrors.street = 'Enter a street address';
    } else if (form.street.includes('@')) {
      newErrors.street = 'Enter a street address without an @ symbol';
    }
    if (!form.city.trim()) {
      newErrors.city = 'Enter a city';
    } else if (!/[a-zA-Z]/.test(form.city)) {
      newErrors.city = 'Enter a city name that includes at least one letter';
    }
    if (!form.state) newErrors.state = 'Select a state';
    if (!form.zipCode.trim()) {
      newErrors.zipCode = 'Enter a ZIP code';
    } else if (!/^\d{5}(-\d{4})?$/.test(form.zipCode.trim())) {
      newErrors.zipCode = 'Enter a 5-digit ZIP or ZIP+4 like 10001-1234';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      setSubmitAttempt((n) => n + 1);
      return;
    }
    await onSubmit(form);
  };

  const isDisabled = disabled || isSubmitting;

  const canSubmit =
    form.caseId.trim() &&
    form.firstName.trim() &&
    form.lastName.trim() &&
    form.email.trim() &&
    form.paymentMethod &&
    form.street.trim() &&
    form.city.trim() &&
    form.state &&
    form.zipCode.trim() &&
    (form.paymentMethod !== 'ACH' ||
      (form.bankRoutingNumber.trim() && form.bankAccountNumber.trim()));

  return (
    <>
      <form ref={formRef} onSubmit={handleSubmit} className="space-y-5" noValidate>
        <FormErrorSummary
          errors={errorsToSummaryItems(errors, error)}
          attempt={submitAttempt}
          onAnnounced={() => focusFirstInvalid(formRef.current)}
        />
        <SectionHeader first>Individual Information</SectionHeader>
        <FormField label={externalRefLabel} required error={errors.caseId}>
          <FormControl>
            <Input
              placeholder={externalRefLabel}
              value={form.caseId}
              onChange={(e) =>
                updateField('caseId', e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))
              }
              disabled={isDisabled}
            />
          </FormControl>
        </FormField>
        <div className="grid grid-cols-2 gap-4">
          <FormField label="First Name" required error={errors.firstName}>
            <FormControl>
              <Input
                placeholder="First Name"
                value={form.firstName}
                onChange={(e) => updateField('firstName', e.target.value)}
                disabled={isDisabled}
              />
            </FormControl>
          </FormField>
          <FormField label="Last Name" required error={errors.lastName}>
            <FormControl>
              <Input
                placeholder="Last Name"
                value={form.lastName}
                onChange={(e) => updateField('lastName', e.target.value)}
                disabled={isDisabled}
              />
            </FormControl>
          </FormField>
        </div>

        <FormField label="Payment Method" required error={errors.paymentMethod}>
          <Select
            value={form.paymentMethod}
            onValueChange={(v) => updateField('paymentMethod', v)}
            disabled={isDisabled}
          >
            <FormControl>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select payment method" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              <SelectItem value="ACH">ACH</SelectItem>
              <SelectItem value="CHECK">Check</SelectItem>
              <SelectItem value="DIGITAL_WALLET">Digital Wallet</SelectItem>
            </SelectContent>
          </Select>
        </FormField>

        {form.paymentMethod === 'ACH' && (
          <div className="space-y-4 rounded-md border p-4 bg-muted/30">
            <h3 className="text-sm font-semibold text-foreground">Bank Account Details</h3>
            <FormField label="Account Type">
              <Select
                value={form.bankAccountType}
                onValueChange={(v) => updateField('bankAccountType', v as 'CHECKING' | 'SAVINGS')}
                disabled={isDisabled}
              >
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="CHECKING">Checking</SelectItem>
                  <SelectItem value="SAVINGS">Savings</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Bank Routing Number" required error={errors.bankRoutingNumber}>
                <FormControl>
                  <Input
                    placeholder="9-digit routing number"
                    value={form.bankRoutingNumber}
                    onChange={(e) =>
                      updateField('bankRoutingNumber', e.target.value.replace(/\D/g, ''))
                    }
                    disabled={isDisabled}
                    maxLength={9}
                  />
                </FormControl>
              </FormField>
              <FormField label="Account Number" required error={errors.bankAccountNumber}>
                <FormControl>
                  <Input
                    placeholder="Account number"
                    value={form.bankAccountNumber}
                    onChange={(e) =>
                      updateField('bankAccountNumber', e.target.value.replace(/\D/g, ''))
                    }
                    disabled={isDisabled}
                    maxLength={17}
                  />
                </FormControl>
              </FormField>
            </div>
          </div>
        )}

        <SectionHeader>Address</SectionHeader>
        <AddressFormFields
          address={{
            address1: form.street,
            address2: form.address2,
            city: form.city,
            state: form.state,
            zipCode: form.zipCode,
          }}
          errors={{
            address1: errors.street,
            address2: errors.address2,
            city: errors.city,
            state: errors.state,
            zipCode: errors.zipCode,
          }}
          onChange={handleAddressChange}
          onVerificationResult={handleAddressVerificationResult}
          onVerificationStatusChange={(status) =>
            setForm((prev) => ({ ...prev, addressVerificationStatus: status }))
          }
          disabled={isDisabled}
          partyId={partyId ?? null}
          accountId={accountId ?? null}
        />

        <SectionHeader>Contact</SectionHeader>
        <FormField label="Email" required error={errors.email}>
          <FormControl>
            <Input
              type="email"
              placeholder="email@example.com"
              value={form.email}
              onChange={(e) => updateField('email', e.target.value)}
              disabled={isDisabled}
            />
          </FormControl>
        </FormField>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="flex gap-2 pt-2">
          {mode === 'edit' && onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} disabled={isDisabled}>
              Cancel
            </Button>
          )}
          <Button type="submit" disabled={isDisabled || !canSubmit}>
            {isSubmitting ? 'Saving…' : submitLabel}
          </Button>
        </div>
      </form>

      <AddressVerificationDialog
        open={showAddressDialog}
        onOpenChange={setShowAddressDialog}
        verificationResult={addressVerificationResult}
        onAcceptSuggested={handleAcceptSuggestedAddress}
        onKeepOriginal={handleKeepOriginalAddress}
      />
    </>
  );
};
